import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Headphones, Home, Mic, Plane, Radio, RotateCcw, ShieldCheck, Volume2, Wifi } from "lucide-react";
import StepHeader from "@/components/StepHeader";
import { PreQuizContactModal, isContactConfirmed } from "@/components/AdvisorForms";
import { findOption, readAdvisorState, resetAdvisorState, writeAdvisorState } from "@/data/polyFitData";
import { isPreviewMode, useAdvisorContent } from "@/hooks";
import { trackAdvisorEvent } from "@/utils/analytics";

const iconMap = { desk: Headphones, home: Home, travel: Plane, wired: Radio, wireless: Wifi, mic: Mic, noise: Volume2, anc: ShieldCheck, headset: Headphones };

function optionTestId(optionId) {
  return `option-card-${optionId}`;
}

export default function FitQuestionPage() {
  const { questionId } = useParams();
  const navigate = useNavigate();
  const { questions, products } = useAdvisorContent();
  const [advisorState, setAdvisorState] = useState(() => readAdvisorState());
  const [selectedOptionId, setSelectedOptionId] = useState(() => readAdvisorState().answers?.[questionId] || "");
  const [contactOpen, setContactOpen] = useState(() => questionId === "workplace" && !isContactConfirmed() && !isPreviewMode());
  const question = questions[questionId] || questions.workplace;

  useEffect(() => {
    if (!questions[questionId]) navigate("/question/workplace", { replace: true });
    if (questionId === "workplace" && !isContactConfirmed() && !isPreviewMode()) setContactOpen(true);
  }, [questionId, navigate, questions]);

  useEffect(() => {
    const nextState = { ...readAdvisorState(), currentQuestionId: question.id };
    setAdvisorState(nextState);
    setSelectedOptionId(nextState.answers?.[question.id] || "");
    writeAdvisorState(nextState);
    window.scrollTo({ top: 0, behavior: "instant" });
    trackAdvisorEvent("question_viewed", { sessionId: nextState.sessionId, question_id: question.id, progress_label: question.progressLabel });
  }, [question.id, question.progressLabel]);

  const step = useMemo(() => Number((question.progressLabel || "").match(/\d/)?.[0]) || 3, [question.progressLabel]);

  const selectOption = (option) => {
    const oldState = readAdvisorState();
    const current = [].concat(oldState.answers?.[question.id] || []);
    let picked;
    if (question.multiSelect) picked = current.includes(option.id) ? current.filter((id) => id !== option.id) : [...current, option.id];
    else {
      if (current[0] === option.id && selectedOptionId === option.id) return;
      picked = [option.id];
    }
    const nextAnswers = { ...oldState.answers, [question.id]: question.multiSelect ? picked : picked[0] };
    const nextLabels = { ...oldState.answerLabels, [question.prompt]: question.options.filter((o) => picked.includes(o.id)).map((o) => o.label).join(", ") };
    const state = { ...oldState, answers: nextAnswers, answerLabels: nextLabels, currentQuestionId: question.id };
    setSelectedOptionId(question.multiSelect ? picked : picked[0] || "");
    setAdvisorState(state);
    writeAdvisorState(state);
    trackAdvisorEvent("option_selected", { sessionId: state.sessionId, question_id: question.id, option_id: option.id, option_label: option.label });
  };

  const pickedIds = [].concat(selectedOptionId || []);
  const continueWithSelection = () => {
    const option = question.options.find((item) => pickedIds.includes(item.id));
    if (!option) return;
    const oldState = readAdvisorState();
    const trimmedPath = oldState.path.includes(question.id) ? oldState.path.slice(0, oldState.path.indexOf(question.id) + 1) : [...oldState.path, question.id];
    const state = { ...oldState, path: trimmedPath, currentQuestionId: question.id };
    if (question.id === "workplace") {
      trackAdvisorEvent("branch_selected", { sessionId: state.sessionId, branch: option.id, branch_label: option.label });
      writeAdvisorState(state);
      setAdvisorState(state);
      navigate(`/scenario/${option.id}`);
      return;
    }
    if (option.nextQuestionId) {
      const routed = { ...state, currentQuestionId: option.nextQuestionId, recommendationId: "" };
      writeAdvisorState(routed);
      setAdvisorState(routed);
      navigate(`/question/${option.nextQuestionId}`);
      return;
    }
    const product = products[option.resultProductId];
    const completed = { ...state, recommendationId: option.resultProductId, completedSteps: option.completedSteps || step, resultProductName: product?.brandName };
    writeAdvisorState(completed);
    setAdvisorState(completed);
    trackAdvisorEvent("branch_completed", { sessionId: completed.sessionId, result_product_id: option.resultProductId, completed_steps: completed.completedSteps });
    navigate("/recommendation");
  };

  const goBack = () => {
    const current = readAdvisorState();
    const path = current.path || [];
    if (question.id === "workplace" || path.length === 0) {
      const nextState = { ...current, currentQuestionId: "workplace", recommendationId: "" };
      writeAdvisorState(nextState);
      setAdvisorState(nextState);
      navigate("/");
      return;
    }
    const previous = path[path.length - 1];
    const cleanedPath = path.slice(0, -1);
    const keepAnswerKeys = [...cleanedPath, previous];
    const cleanedAnswers = Object.fromEntries(Object.entries(current.answers || {}).filter(([key]) => keepAnswerKeys.includes(key)));
    const cleanedLabels = {};
    keepAnswerKeys.forEach((qid) => {
      const option = questions[qid]?.options?.find((item) => item.id === cleanedAnswers[qid]) || findOption(qid, cleanedAnswers[qid]);
      if (option && questions[qid]) cleanedLabels[questions[qid].prompt] = option.label;
    });
    const nextState = { ...current, path: cleanedPath, answers: cleanedAnswers, answerLabels: cleanedLabels, currentQuestionId: previous, recommendationId: "" };
    writeAdvisorState(nextState);
    setAdvisorState(nextState);
    navigate(`/question/${previous}`);
  };

  const restart = () => {
    const state = resetAdvisorState();
    trackAdvisorEvent("quiz_restarted", { sessionId: state.sessionId, source: "question" });
    navigate("/");
  };

  return (
    <main className="advisor-page question-page" data-testid="fit-question-page">
      <StepHeader step={step} compact progressLabel={question.progressLabel} />
      <section className="question-stage reveal-in" data-testid="fit-question-shell">
        <header className="question-head" data-testid="fit-question-context">
          <p className="step-kicker" data-testid="fit-step-label">{question.progressLabel}</p>
          <h1 data-testid="fit-question-title">{question.prompt}</h1>
          {question.multiSelect && <p className="question-helper" data-testid="fit-question-helper">{question.helper || "Select all that apply"}</p>}
        </header>
        <div className={`option-list option-count-${question.options.length}`} data-testid="fit-option-list">
          {question.options.map((option) => {
            const Icon = iconMap[option.icon] || Headphones;
            const selected = pickedIds.includes(option.id);
            const testId = optionTestId(option.id);
            const imageUrl = option.imageUrl;
            return (
              <button className={`advisor-option visual-option ${selected ? "checked is-selected" : ""}`} type="button" key={option.id} onClick={() => selectOption(option)} aria-pressed={selected} data-selected={selected ? "true" : "false"} style={imageUrl ? { backgroundImage: `url(${imageUrl})` } : undefined} data-testid={testId}>
                {imageUrl && <img className="option-image" src={imageUrl} alt="" aria-hidden="true" data-testid={`${testId}-image`} />}
                <span className="option-overlay" data-testid={`${testId}-overlay`}>
                  {!imageUrl && <span className="option-icon" data-testid={`${testId}-icon`}><Icon size={22} /></span>}
                  <span className="option-copy" data-testid={`${testId}-copy`}><b>{option.heading || option.label}</b></span>
                  {selected && <span className="option-selected-mark" data-testid={`${testId}-selected-mark`}><CheckCircle2 size={22} /></span>}
                </span>
              </button>
            );
          })}
        </div>
        <div className="question-nav" data-testid="fit-question-forward-bar">
          <div className="question-nav-left" data-testid="fit-question-action-row">
            <button className="text-btn" type="button" onClick={goBack} data-testid="fit-back-button"><ArrowLeft size={16} /> Back</button>
            <button className="text-btn" type="button" onClick={restart} data-testid="fit-restart-button"><RotateCcw size={15} /> Restart</button>
          </div>
          <button className="primary-cta pill-cta" type="button" onClick={continueWithSelection} disabled={!pickedIds.length} data-testid="fit-question-continue-button">Continue <ArrowRight size={17} /></button>
        </div>
      </section>
      <PreQuizContactModal open={contactOpen} onComplete={() => setContactOpen(false)} />
    </main>
  );
}